import axios from 'axios';
import { 
  DashboardStats, 
  PartCategory, 
  CarMake, 
  CarModel, 
  CarYear, 
  EngineType, 
  Part, 
  Order, 
  User, 
  Driver,
  ShippingMethod, 
  Warehouse, 
  Payment,
  PaymentStatus,
  CreatePaymentPayload,
  UpdatePaymentPayload,
  PaymentStats,
  CreatePartPayload,
  UpdatePartPayload,
  Vehicle,
  Brand,
  CountryMade,
  TireSize,
  CreateShippingMethodPayload,
  UpdateShippingMethodPayload,
  ShippingZone,
  ShippingRate,
  Advertisement,
  CreateAdvertisementPayload,
  UpdateAdvertisementPayload,
  AdvertisementStats,
  SystemSettings,
  UpdateSystemSettings,
  TestEmailRequest
} from '../types';

/** Remove trailing slashes so `${base}/api` never becomes `//api`. */
const trimTrailingSlashes = (url: string): string => url.replace(/\/+$/, '');

// Get API base URL from environment variable or use default
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  if (envUrl) {
    const base = trimTrailingSlashes(envUrl);
    return base.endsWith('/api') ? base : `${base}/api`;
  }
  // Default: localhost for development
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Get base URL without /api for image URLs
const getBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  if (envUrl) {
    const base = trimTrailingSlashes(envUrl);
    return base.endsWith('/api') ? base.replace(/\/api$/, '') : base;
  }
  // Default: localhost for development (without /api)
  return 'http://localhost:5000';
};

const BASE_URL = getBaseUrl();

/** Wake Render cold starts so the first CORS OPTIONS (e.g. enginetypes/car-model/N) is less likely to CF 520. */
try {
  if (typeof fetch === 'function') {
    void fetch(`${BASE_URL}/health`, { method: 'GET', mode: 'cors', cache: 'no-store' }).catch(() => undefined);
  }
} catch {
  // best-effort only
}

// Debug: Log the API URL being used
console.log('API_BASE_URL:', API_BASE_URL);
console.log('BASE_URL:', BASE_URL);
console.log('REACT_APP_API_BASE_URL env var:', process.env.REACT_APP_API_BASE_URL);
console.log('NODE_ENV:', process.env.NODE_ENV);

// Utility function to get full image URL (safe for HTTPS dashboards + R2)
export const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';

  let path = imagePath.trim();

  // Already absolute
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Mixed content: upgrade our API http URLs to https when the page is https
    if (
      typeof window !== 'undefined'
      && window.location.protocol === 'https:'
      && path.startsWith('http://')
    ) {
      path = 'https://' + path.slice('http://'.length);
    }
    return path;
  }

  // Legacy local uploads path
  if (path.startsWith('/uploads/')) {
    return `${BASE_URL}${path}`.replace(/^http:\/\//i, (m) =>
      typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https://' : m
    );
  }

  if (path.startsWith('uploads/')) {
    return `${BASE_URL}/${path}`;
  }

  return `${BASE_URL}/uploads/${path}`;
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('authToken');
      const isDevBypass =
        token === 'mock-token-for-development' ||
        (typeof window !== 'undefined' && window.location.search.includes('dev=true'));
      // Demo / local map preview uses a mock token; do not kick the user to login
      // when the API rejects it. Pages already fall back to sample data.
      if (!isDevBypass) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard API
export const dashboardApi = {
  getStats: (): Promise<DashboardStats> => 
    api.get('/dashboard/stats').then(res => res.data),
};

// Part Categories API
export const partCategoriesApi = {
  getAll: (): Promise<PartCategory[]> => 
    api.get('/partcategories').then(res => res.data),
  getById: (id: number): Promise<PartCategory> => 
    api.get(`/partcategories/${id}`).then(res => res.data),
  getRoot: (): Promise<PartCategory[]> => 
    api.get('/partcategories/root').then(res => res.data),
  getSubCategories: (parentId: number): Promise<PartCategory[]> => 
    api.get(`/partcategories/${parentId}/subcategories`).then(res => res.data),
  getFeatured: (): Promise<PartCategory[]> => 
    api.get('/partcategories/featured').then(res => res.data),
  getUserDefined: (): Promise<PartCategory[]> => 
    api.get('/partcategories/user-defined').then(res => res.data),
  search: (query: string): Promise<PartCategory[]> => 
    api.get(`/partcategories/search?q=${query}`).then(res => res.data),
  create: (data: Omit<PartCategory, 'id' | 'createdAt' | 'updatedAt' | 'partsCount'>): Promise<PartCategory> => 
    api.post('/partcategories', data).then(res => res.data),
  update: (id: number, data: Partial<PartCategory>): Promise<PartCategory> => 
    api.put(`/partcategories/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/partcategories/${id}`).then(res => res.data),
};

// Parent Categories API
export const parentCategoriesApi = {
  getAll: (): Promise<PartCategory[]> => 
    api.get('/parentcategories').then(res => res.data),
  getById: (id: number): Promise<PartCategory> => 
    api.get(`/parentcategories/${id}`).then(res => res.data),
  getRoot: (): Promise<PartCategory[]> => 
    api.get('/parentcategories/root').then(res => res.data),
  getFeatured: (): Promise<PartCategory[]> => 
    api.get('/parentcategories/featured').then(res => res.data),
  search: (query: string): Promise<PartCategory[]> => 
    api.get(`/parentcategories/search?q=${query}`).then(res => res.data),
  create: (data: Omit<PartCategory, 'id' | 'createdAt' | 'updatedAt' | 'partsCount'>): Promise<PartCategory> => 
    api.post('/parentcategories', data).then(res => res.data),
  update: (id: number, data: Partial<PartCategory>): Promise<PartCategory> => 
    api.put(`/parentcategories/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/parentcategories/${id}`).then(res => res.data),
};

// Car Makes API
export const carMakesApi = {
  getAll: (): Promise<CarMake[]> => 
    api.get('/carmakes').then(res => res.data),
  getById: (id: number): Promise<CarMake> => 
    api.get(`/carmakes/${id}`).then(res => res.data),
  search: (query: string): Promise<CarMake[]> => 
    api.get(`/carmakes/search?q=${query}`).then(res => res.data),
  create: (data: Omit<CarMake, 'id' | 'createdAt' | 'updatedAt'>): Promise<CarMake> => 
    api.post('/carmakes', data).then(res => res.data),
  update: (id: number, data: Partial<CarMake>): Promise<CarMake> => 
    api.put(`/carmakes/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/carmakes/${id}`).then(res => res.data),
};

// Brands API
export const brandsApi = {
  getAll: (): Promise<Brand[]> => 
    api.get('/Brands').then(res => res.data),
  getAllWithPartsCount: (): Promise<Brand[]> => 
    api.get('/Brands/with-parts-count').then(res => res.data),
  getById: (id: number): Promise<Brand> => 
    api.get(`/Brands/${id}`).then(res => res.data),
  getDropdown: (): Promise<{ value: number; label: string }[]> => 
    api.get('/Brands/dropdown').then(res => res.data),
  search: (query: string): Promise<Brand[]> => 
    api.get(`/Brands/search?q=${query}`).then(res => res.data),
  create: (data: Omit<Brand, 'id' | 'createdAt' | 'updatedAt'>): Promise<Brand> => 
    api.post('/Brands', data).then(res => res.data),
  update: (id: number, data: Partial<Brand>): Promise<Brand> => 
    api.put(`/Brands/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/Brands/${id}`).then(res => res.data),
};

// Country Made API
export const countryMadesApi = {
  getAll: (): Promise<CountryMade[]> => 
    api.get('/CountryMades').then(res => res.data),
  getAllWithPartsCount: (): Promise<CountryMade[]> => 
    api.get('/CountryMades/with-parts-count').then(res => res.data),
  getById: (id: number): Promise<CountryMade> => 
    api.get(`/CountryMades/${id}`).then(res => res.data),
  getDropdown: (): Promise<{ value: number; label: string }[]> => 
    api.get('/CountryMades/dropdown').then(res => res.data),
  search: (query: string): Promise<CountryMade[]> => 
    api.get(`/CountryMades/search?name=${query}`).then(res => res.data),
  create: (data: Omit<CountryMade, 'id' | 'createdAt' | 'updatedAt' | 'partsCount'>): Promise<CountryMade> => 
    api.post('/CountryMades', data).then(res => res.data),
  update: (id: number, data: Partial<CountryMade>): Promise<CountryMade> => 
    api.put(`/CountryMades/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/CountryMades/${id}`).then(res => res.data),
};

// Car Models API
export const carModelsApi = {
  getAll: (): Promise<CarModel[]> => 
    api.get('/carmodels').then(res => res.data),
  getById: (id: number): Promise<CarModel> => 
    api.get(`/carmodels/${id}`).then(res => res.data),
  getByMake: (makeId: number): Promise<CarModel[]> => 
    api.get(`/carmodels/make/${makeId}`).then(res => res.data),
  search: (query: string): Promise<CarModel[]> => 
    api.get(`/carmodels/search?q=${query}`).then(res => res.data),
  create: (data: Omit<CarModel, 'id' | 'createdAt' | 'updatedAt' | 'carMakeName'>): Promise<CarModel> => 
    api.post('/carmodels', data).then(res => res.data),
  update: (id: number, data: Partial<CarModel>): Promise<CarModel> => 
    api.put(`/carmodels/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/carmodels/${id}`).then(res => res.data),
};

// Car Years API
export const carYearsApi = {
  getAll: (): Promise<CarYear[]> => 
    api.get('/caryears').then(res => res.data),
  getById: (id: number): Promise<CarYear> => 
    api.get(`/caryears/${id}`).then(res => res.data),
  search: (query: string): Promise<CarYear[]> => 
    api.get(`/caryears/search?q=${query}`).then(res => res.data),
  create: (data: Omit<CarYear, 'id' | 'createdAt' | 'updatedAt'>): Promise<CarYear> => 
    api.post('/caryears', data).then(res => res.data),
  update: (id: number, data: Partial<CarYear>): Promise<CarYear> => 
    api.put(`/caryears/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/caryears/${id}`).then(res => res.data),
};

// Engine Types API
export const engineTypesApi = {
  getAll: (): Promise<EngineType[]> => 
    api.get('/enginetypes').then(res => res.data),
  getById: (id: number): Promise<EngineType> => 
    api.get(`/enginetypes/${id}`).then(res => res.data),
  getByCarModel: (carModelId: number): Promise<EngineType[]> => 
    api.get(`/enginetypes/car-model/${carModelId}`).then(res => res.data),
  getByCarMake: (carMakeId: number): Promise<EngineType[]> => 
    api.get(`/enginetypes/car-make/${carMakeId}`).then(res => res.data),
  search: (query: string): Promise<EngineType[]> => 
    api.get(`/enginetypes/search?q=${query}`).then(res => res.data),
  create: (data: Omit<EngineType, 'id' | 'createdAt' | 'updatedAt' | 'carModelName' | 'carMakeName'>): Promise<EngineType> => 
    api.post('/enginetypes', data).then(res => res.data),
  update: (id: number, data: Partial<EngineType>): Promise<EngineType> => 
    api.put(`/enginetypes/${id}`, data).then(res => res.data),
  renameCode: (data: {
    carModelId: number;
    oldCode: string;
    newCode: string;
  }): Promise<{ updatedCount: number; oldCode: string; newCode: string }> =>
    api.post('/enginetypes/rename-code', data).then(res => res.data),
  /** Admin: merge "Name (2)" engines into canonical "Name"; remaps vehicles/parts safely. */
  mergeSuffixDuplicates: (): Promise<{
    modelsTouched: number;
    engineGroupsMerged: number;
    enginesSoftDeleted: number;
    vehiclesRemapped: number;
    vehiclesSoftDeleted: number;
    partLinksMoved: number;
    userVehiclesRemapped: number;
    enginesRenamedToBase: number;
    details: string[];
  }> => api.post('/enginetypes/merge-suffix-duplicates').then((res) => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/enginetypes/${id}`).then(res => res.data),
};

// Parts API
export const partsApi = {
  /** Get all parts. Prefer getAdminList for the dashboard table (paginated). */
  getAll: (includeInactive = false): Promise<Part[]> =>
    api.get(`/parts/all?includeInactive=${includeInactive}`).then(res => res.data),
  /** Paginated admin list — use this on Parts page to avoid loading the full catalog. */
  getAdminList: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    brandId?: number;
    categoryId?: number;
    isActive?: boolean;
  } = {}): Promise<{
    items: Part[];
    totalCount: number;
    page: number;
    pageSize: number;
  }> => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('pageSize', String(params.pageSize ?? 25));
    if (params.search?.trim()) q.set('search', params.search.trim());
    if (params.brandId != null) q.set('brandId', String(params.brandId));
    if (params.categoryId != null) q.set('categoryId', String(params.categoryId));
    if (params.isActive != null) q.set('isActive', String(params.isActive));
    return api.get(`/parts/admin-list?${q.toString()}`).then((res) => res.data);
  },
  getById: (id: number): Promise<Part> => 
    api.get(`/parts/${id}`).then(res => res.data),
  getByCategory: (categoryId: number): Promise<Part[]> => 
    api.get(`/parts/category/${categoryId}`).then(res => res.data),
  getByBrand: (brand: string): Promise<Part[]> => 
    api.get(`/parts/brand/${brand}`).then(res => res.data),
  getFeatured: (): Promise<Part[]> => 
    api.get('/parts/featured').then(res => res.data),
  getOnSale: (): Promise<Part[]> => 
    api.get('/parts/on-sale').then(res => res.data),
  getLowStock: (): Promise<Part[]> => 
    api.get('/parts/low-stock').then(res => res.data),
  search: (query: string): Promise<Part[]> => 
    api.get(`/parts/search?q=${query}`).then(res => res.data),
  create: (data: CreatePartPayload): Promise<Part> => 
    api.post('/parts', data).then(res => res.data),
  update: (id: number, data: UpdatePartPayload): Promise<Part> => 
    api.put(`/parts/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/parts/${id}`).then(res => res.data),
  rebuildDescriptions: (options?: {
    includeModel?: boolean;
    includeYears?: boolean;
    includeEngines?: boolean;
    includeTireSizes?: boolean;
  }): Promise<{
    totalActiveParts: number;
    updated: number;
    unchanged: number;
    failed: number;
    message: string;
  }> =>
    api
      .post('/parts/rebuild-descriptions', {
        includeModel: options?.includeModel ?? true,
        includeYears: options?.includeYears ?? true,
        includeEngines: options?.includeEngines ?? true,
        includeTireSizes: options?.includeTireSizes ?? true,
      })
      .then((res) => res.data),
  exportToExcel: (params: {
    search?: string;
    brandId?: number;
    categoryId?: number;
    isActive?: boolean;
    tireSizeIds?: number[];
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.brandId != null) queryParams.append('brandId', String(params.brandId));
    if (params.categoryId != null) queryParams.append('categoryId', String(params.categoryId));
    if (params.isActive != null) queryParams.append('isActive', String(params.isActive));
    params.tireSizeIds?.forEach((id) => queryParams.append('tireSizeIds', String(id)));
    return api.get(`/parts/export/excel?${queryParams.toString()}`, {
      responseType: 'blob',
    }).then(res => res.data);
  },
  exportToPdf: (params: {
    search?: string;
    brandId?: number;
    categoryId?: number;
    isActive?: boolean;
    tireSizeIds?: number[];
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.brandId != null) queryParams.append('brandId', String(params.brandId));
    if (params.categoryId != null) queryParams.append('categoryId', String(params.categoryId));
    if (params.isActive != null) queryParams.append('isActive', String(params.isActive));
    params.tireSizeIds?.forEach((id) => queryParams.append('tireSizeIds', String(id)));
    return api.get(`/parts/export/pdf?${queryParams.toString()}`, {
      responseType: 'blob',
    }).then(res => res.data);
  },
  importLegacyProducts: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/parts/import-legacy-products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(res => res.data);
  },
};

// Tire / rim diameter sizes (15, 16, 17, …)
export const tireSizesApi = {
  getAll: (includeInactive = false): Promise<TireSize[]> =>
    api.get(`/TireSizes?includeInactive=${includeInactive}`).then(res => res.data),
  getById: (id: number): Promise<TireSize> =>
    api.get(`/TireSizes/${id}`).then(res => res.data),
};

// Orders API
export const ordersApi = {
  getAll: (): Promise<Order[]> => 
    api.get('/orders').then(res => res.data),
  getPaginated: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    driverId?: number;
    unassignedOnly?: boolean;
  } = {}): Promise<{
    data: Order[];
    page: number;
    pageSize: number;
    totalCount: number;
  }> => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('pageSize', String(params.pageSize ?? 25));
    if (params.search) q.set('search', params.search);
    if (params.status && params.status !== 'all') q.set('status', params.status);
    if (params.driverId != null) q.set('driverId', String(params.driverId));
    if (params.unassignedOnly) q.set('unassignedOnly', 'true');
    return api.get(`/orders/paginated?${q.toString()}`).then((res) => res.data);
  },
  /** Recent orders for Payments create dialog — not the full orders dump. */
  getUnpaidCandidates: (take = 50): Promise<Order[]> =>
    api.get('/orders/unpaid', { params: { take } }).then((res) => res.data),
  getById: (id: number): Promise<Order> => 
    api.get(`/orders/${id}`).then(res => res.data),
  getByUser: (userId: number): Promise<Order[]> => 
    api.get(`/orders/user/${userId}`).then(res => res.data),
  getByStatus: (status: string): Promise<Order[]> => 
    api.get(`/orders/status/${status}`).then(res => res.data),
  getByDriver: (driverId: number): Promise<Order[]> => 
    api.get(`/orders/driver/${driverId}`).then(res => res.data),
  create: (data: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Promise<Order> => 
    api.post('/orders', data).then(res => res.data),
  update: (id: number, data: Partial<Order>): Promise<Order> => {
    console.log('=== FRONTEND API UPDATE DEBUG ===');
    console.log('Order ID:', id);
    console.log('Data being sent:', data);
    console.log('Data type:', typeof data);
    console.log('Data JSON:', JSON.stringify(data));
    console.log('================================');
    return api.put(`/orders/${id}`, data).then(res => res.data);
  },
  delete: (id: number): Promise<void> => 
    api.delete(`/orders/${id}`).then(res => res.data),
  getDrivers: (): Promise<Driver[]> => 
    api.get('/orders/drivers').then(res => res.data),
  assignDriver: (orderId: number, driverId: number): Promise<Order> => 
    api.post(`/orders/${orderId}/assign-driver`, { driverId }).then(res => res.data),
  unassignDriver: (orderId: number): Promise<Order> => 
    api.post(`/orders/${orderId}/unassign-driver`).then(res => res.data),
};

export interface GeocodeResponse {
  addressId?: number;
  latitude: number;
  longitude: number;
  formattedAddress: string;
  persisted: boolean;
}

export const geocodingApi = {
  resolveSavedAddress: (addressId: number): Promise<GeocodeResponse> =>
    api.post(`/geocoding/addresses/${addressId}`).then(res => res.data),
  /** Resolve coords for an OrderDeliveryAddresses snapshot (dashboard order shippingAddress.id). */
  resolveOrderDeliveryAddress: (snapshotId: number): Promise<GeocodeResponse> =>
    api.post(`/geocoding/order-delivery-addresses/${snapshotId}`).then(res => res.data),
};

// Users API
export const usersApi = {
  getAll: (): Promise<User[]> => 
    api.get('/users').then(res => res.data),
  getById: (id: number): Promise<User> => 
    api.get(`/users/${id}`).then(res => res.data),
  getByRole: (role: string): Promise<User[]> => 
    api.get(`/users/role/${role}`).then(res => res.data),
  create: (data: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'roles'>): Promise<User> => 
    api.post('/users', data).then(res => res.data),
  update: (id: number, data: Partial<User>): Promise<User> => 
    api.put(`/users/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/users/${id}`).then(res => res.data),
};


// Warehouses API
export const warehousesApi = {
  getAll: (): Promise<Warehouse[]> => 
    api.get('/Warehouses').then(res => res.data),
  getById: (id: number): Promise<Warehouse> => 
    api.get(`/Warehouses/${id}`).then(res => res.data),
  getDefault: (): Promise<Warehouse> => 
    api.get('/Warehouses/default').then(res => res.data),
  getMain: (): Promise<Warehouse> => 
    api.get('/Warehouses/main').then(res => res.data),
  create: (data: Omit<Warehouse, 'id' | 'createdAt' | 'updatedAt'>): Promise<Warehouse> => 
    api.post('/Warehouses', data).then(res => res.data),
  update: (id: number, data: Partial<Warehouse>): Promise<Warehouse> => 
    api.put(`/Warehouses/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/Warehouses/${id}`).then(res => res.data),
};

// Image Upload API
export type WatermarkLogoTone = 'Auto' | 'White' | 'Black';

export const imageUploadApi = {
  upload: async (
    formData: FormData,
    folder: string = 'parts',
    options?: { sizePercent?: number; opacityPercent?: number; logoTone?: WatermarkLogoTone }
  ) => {
    const params = new URLSearchParams({ folder });
    if (options?.sizePercent != null) params.set('sizePercent', String(options.sizePercent));
    if (options?.opacityPercent != null) params.set('opacityPercent', String(options.opacityPercent));
    if (options?.logoTone) params.set('logoTone', options.logoTone);
    const response = await api.post(`/imageupload/upload?${params.toString()}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  delete: async (imageUrl: string) => {
    const response = await api.delete(`/imageupload/delete?imageUrl=${encodeURIComponent(imageUrl)}`);
    return response.data;
  },

  getUrl: async (fileName: string, folder: string = 'parts') => {
    const response = await api.get(`/imageupload/url?fileName=${fileName}&folder=${folder}`);
    return response.data;
  },
};

/** Live watermark preview for parts add/edit (does not write storage). */
export const partImageWatermarkApi = {
  preview: async (options: {
    sizePercent?: number;
    opacityPercent?: number;
    logoTone?: WatermarkLogoTone;
    partImageId?: number | null;
    sampleFile?: File | null;
  }): Promise<Blob> => {
    const form = new FormData();
    form.append('sizePercent', String(options.sizePercent ?? 100));
    form.append('opacityPercent', String(options.opacityPercent ?? 40));
    form.append('logoTone', options.logoTone ?? 'Auto');
    if (options.partImageId != null) form.append('partImageId', String(options.partImageId));
    if (options.sampleFile) form.append('sample', options.sampleFile);
    const response = await api.post('/admin/part-image-watermarks/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Vehicles API
export const vehiclesApi = {
  getAll: (): Promise<Vehicle[]> => 
    api.get('/vehicles/all').then(res => res.data),
  getById: (id: number): Promise<Vehicle> => 
    api.get(`/vehicles/${id}`).then(res => res.data),
  getByMake: (makeId: number): Promise<Vehicle[]> => 
    api.get(`/vehicles/make/${makeId}`).then(res => res.data),
  getByModel: (modelId: number): Promise<Vehicle[]> => 
    api.get(`/vehicles/model/${modelId}`).then(res => res.data),
  getByYear: (yearId: number): Promise<Vehicle[]> => 
    api.get(`/vehicles/year/${yearId}`).then(res => res.data),
  /** Years assigned per engine for one make/model (catalog UI — avoids /vehicles/all). */
  getEngineYears: (
    carMakeId: number,
    carModelId: number
  ): Promise<{ engineTypeId: number; years: string[] }[]> =>
    api
      .get('/vehicles/engine-years', { params: { carMakeId, carModelId } })
      .then((res) => res.data),
  /** Slim vehicles for Parts chips / edit — never use getAll for this. */
  getByIds: (ids: number[]): Promise<Vehicle[]> => {
    const unique = Array.from(new Set(ids.filter((id) => id > 0)));
    if (unique.length === 0) return Promise.resolve([]);
    return api
      .get('/vehicles/by-ids', { params: { ids: unique.join(',') } })
      .then((res) => res.data);
  },
  /** Vehicles for one make+model (Parts picker). */
  getCatalog: (carMakeId: number, carModelId: number): Promise<Vehicle[]> =>
    api
      .get('/vehicles/catalog', { params: { carMakeId, carModelId } })
      .then((res) => res.data),
  search: (make?: string, model?: string, year?: string): Promise<Vehicle[]> => {
    const params = new URLSearchParams();
    if (make) params.append('make', make);
    if (model) params.append('model', model);
    if (year) params.append('year', year);
    return api.get(`/vehicles/search?${params.toString()}`).then(res => res.data);
  },
  create: (data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>): Promise<Vehicle> => 
    api.post('/vehicles', data).then(res => res.data),
  update: (id: number, data: Partial<Vehicle>): Promise<Vehicle> => 
    api.put(`/vehicles/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/vehicles/${id}`).then(res => res.data),
  syncEngineYears: (data: {
    carMakeId: number;
    carModelId: number;
    engineTypeId: number;
    years: string[];
    trim?: string;
    transmission?: string;
    bodyStyle?: string;
    removeUnselected?: boolean;
  }): Promise<{
    created: number;
    reactivated: number;
    removed: number;
    skippedRemoval: number;
    assignedYears: string[];
  }> => api.post('/vehicles/sync-engine-years', data).then(res => res.data),
};

// Icon upload API
export const iconApi = {
  upload: (file: File, folder: string = 'categories'): Promise<{ iconUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    return api.post('/icon/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
  
  delete: (iconUrl: string): Promise<{ success: boolean }> => {
    return api.delete('/icon/delete', {
      params: { iconUrl }
    }).then(res => res.data);
  },
  
  getAvailable: (folder: string = 'categories'): Promise<string[]> => {
    return api.get('/icon/categories').then(res => res.data);
  }
};

// Shipping Methods API
export const shippingMethodsApi = {
  getAll: (): Promise<ShippingMethod[]> => 
    api.get('/shippingmethods').then(res => res.data),
  getById: (id: number): Promise<ShippingMethod> => 
    api.get(`/shippingmethods/${id}`).then(res => res.data),
  getActive: (): Promise<ShippingMethod[]> => 
    api.get('/shippingmethods/active').then(res => res.data),
  getDefault: (): Promise<ShippingMethod> => 
    api.get('/shippingmethods/default').then(res => res.data),
  getByCarrier: (carrier: string): Promise<ShippingMethod[]> => 
    api.get(`/shippingmethods/carrier/${carrier}`).then(res => res.data),
  search: (query: string): Promise<ShippingMethod[]> => 
    api.get(`/shippingmethods/search?q=${query}`).then(res => res.data),
  create: (data: CreateShippingMethodPayload): Promise<ShippingMethod> => 
    api.post('/shippingmethods', data).then(res => res.data),
  update: (id: number, data: UpdateShippingMethodPayload): Promise<ShippingMethod> => 
    api.put(`/shippingmethods/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/shippingmethods/${id}`).then(res => res.data),
  setDefault: (id: number): Promise<void> => 
    api.put(`/shippingmethods/${id}/set-default`).then(res => res.data),
  reorder: (methodIds: number[]): Promise<void> => 
    api.put('/shippingmethods/reorder', { methodIds }).then(res => res.data),
};

// Shipping Zones API
export const shippingZonesApi = {
  getAll: (): Promise<ShippingZone[]> => 
    api.get('/shippingzones').then(res => res.data),
  getById: (id: number): Promise<ShippingZone> => 
    api.get(`/shippingzones/${id}`).then(res => res.data),
  getActive: (): Promise<ShippingZone[]> => 
    api.get('/shippingzones/active').then(res => res.data),
  create: (data: Omit<ShippingZone, 'id' | 'createdAt' | 'updatedAt'>): Promise<ShippingZone> => 
    api.post('/shippingzones', data).then(res => res.data),
  update: (id: number, data: Partial<ShippingZone>): Promise<ShippingZone> => 
    api.put(`/shippingzones/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/shippingzones/${id}`).then(res => res.data),
};

// Shipping Rates API
export const shippingRatesApi = {
  getAll: (): Promise<ShippingRate[]> => 
    api.get('/shippingrates').then(res => res.data),
  getById: (id: number): Promise<ShippingRate> => 
    api.get(`/shippingrates/${id}`).then(res => res.data),
  getByMethod: (methodId: number): Promise<ShippingRate[]> => 
    api.get(`/shippingrates/method/${methodId}`).then(res => res.data),
  getByZone: (zoneId: number): Promise<ShippingRate[]> => 
    api.get(`/shippingrates/zone/${zoneId}`).then(res => res.data),
  create: (data: Omit<ShippingRate, 'id' | 'createdAt' | 'updatedAt'>): Promise<ShippingRate> => 
    api.post('/shippingrates', data).then(res => res.data),
  update: (id: number, data: Partial<ShippingRate>): Promise<ShippingRate> => 
    api.put(`/shippingrates/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/shippingrates/${id}`).then(res => res.data),
  calculate: (data: {
    methodId: number;
    weight: number;
    distance?: number;
    orderValue: number;
    destination: {
      country: string;
      state?: string;
      city?: string;
      postalCode?: string;
    };
  }): Promise<{ rate: number; estimatedDays: number }> => 
    api.post('/shippingrates/calculate', data).then(res => res.data),
};

// Payments API
export const paymentsApi = {
  getAll: (): Promise<Payment[]> => 
    api.get('/payments').then(res => res.data),
  getById: (id: number): Promise<Payment> => 
    api.get(`/payments/${id}`).then(res => res.data),
  getByNumber: (paymentNumber: string): Promise<Payment> => 
    api.get(`/payments/number/${paymentNumber}`).then(res => res.data),
  getByOrder: (orderId: number): Promise<Payment[]> => 
    api.get(`/payments/order/${orderId}`).then(res => res.data),
  getByStatus: (status: PaymentStatus): Promise<Payment[]> => 
    api.get(`/payments/status/${status}`).then(res => res.data),
  getByMethod: (paymentMethod: string): Promise<Payment[]> => 
    api.get(`/payments/method/${paymentMethod}`).then(res => res.data),
  getByDateRange: (startDate: string, endDate: string): Promise<Payment[]> => 
    api.get(`/payments/date-range?startDate=${startDate}&endDate=${endDate}`).then(res => res.data),
  getPaginated: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: PaymentStatus;
    paymentMethod?: string;
  }): Promise<{
    payments: Payment[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status.toString());
    if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
    
    return api.get(`/payments/paginated?${queryParams.toString()}`).then(res => res.data);
  },
  getStats: (): Promise<PaymentStats> => 
    api.get('/payments/stats').then(res => res.data),
  create: (data: CreatePaymentPayload): Promise<Payment> => 
    api.post('/payments', data).then(res => res.data),
  update: (id: number, data: UpdatePaymentPayload): Promise<void> => 
    api.put(`/payments/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/payments/${id}`).then(res => res.data),
  process: (id: number): Promise<void> => 
    api.post(`/payments/${id}/process`).then(res => res.data),
  refund: (id: number, reason?: string): Promise<void> => 
    api.post(`/payments/${id}/refund`, reason).then(res => res.data),
  cancel: (id: number, reason?: string): Promise<void> => 
    api.post(`/payments/${id}/cancel`, reason).then(res => res.data),
  exportToExcel: (params: {
    search?: string;
    status?: PaymentStatus;
    paymentMethod?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
    
    return api.get(`/payments/export/excel?${queryParams.toString()}`, {
      responseType: 'blob'
    }).then(res => res.data);
  },
  exportToPdf: (params: {
    search?: string;
    status?: PaymentStatus;
    paymentMethod?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
    
    return api.get(`/payments/export/pdf?${queryParams.toString()}`, {
      responseType: 'blob'
    }).then(res => res.data);
  },
};

// Advertisements API
export const advertisementsApi = {
  getAll: (): Promise<Advertisement[]> => 
    api.get('/advertisements').then(res => res.data),
  getById: (id: number): Promise<Advertisement> => 
    api.get(`/advertisements/${id}`).then(res => res.data),
  getActive: (): Promise<Advertisement[]> => 
    api.get('/advertisements/getAdds').then(res => res.data),
  getStats: (): Promise<AdvertisementStats> => 
    api.get('/advertisements/stats').then(res => res.data),
  create: (data: CreateAdvertisementPayload): Promise<Advertisement> => 
    api.post('/advertisements', data).then(res => res.data),
  update: (id: number, data: UpdateAdvertisementPayload): Promise<Advertisement> => 
    api.put(`/advertisements/${id}`, data).then(res => res.data),
  delete: (id: number): Promise<void> => 
    api.delete(`/advertisements/${id}`).then(res => res.data),
  toggleStatus: (id: number): Promise<Advertisement> => 
    api.patch(`/advertisements/${id}/toggle`).then(res => res.data),
};

// Notifications API
export const notificationsApi = {
  sendToUsers: (data: {
    userIds?: number[];
    topic?: string;
    title: string;
    body: string;
    data?: { [key: string]: string };
  }): Promise<{
    success: boolean;
    message: string;
    sentCount?: number;
    totalCount?: number;
  }> => api.post('/messages/admin/sendNotification', data).then(res => res.data),
  
  sendToUser: (userId: number, data: {
    title: string;
    body: string;
    data?: { [key: string]: string };
  }): Promise<{
    success: boolean;
    message: string;
    sentCount?: number;
    totalCount?: number;
  }> => api.post(`/messages/admin/sendNotification/${userId}`, data).then(res => res.data),
  
  sendToTopic: (data: {
    topic: string;
    title: string;
    body: string;
    data?: { [key: string]: string };
  }): Promise<{
    success: boolean;
    message: string;
  }> => api.post('/messages/admin/sendTopicNotification', data).then(res => res.data),
};

// System Settings API
// Helper function to transform SystemSettingsDto from PascalCase to camelCase
const transformSettingsFromBackend = (data: any): SystemSettings => {
  return {
    appName: data.AppName,
    baseUrl: data.BaseUrl,
    defaultLanguage: data.DefaultLanguage,
    timeZone: data.TimeZone,
    dateFormat: data.DateFormat,
    timeFormat: data.TimeFormat,
    smtpHost: data.SmtpHost,
    smtpPort: data.SmtpPort,
    smtpUsername: data.SmtpUsername,
    smtpPassword: data.SmtpPassword,
    smtpEnableSsl: data.SmtpEnableSsl,
    fromEmail: data.FromEmail,
    fromName: data.FromName,
    jwtKey: data.JwtKey,
    jwtIssuer: data.JwtIssuer,
    jwtAudience: data.JwtAudience,
    jwtExpiryInMinutes: data.JwtExpiryInMinutes,
    googleClientId: data.GoogleClientId,
    googleClientSecret: data.GoogleClientSecret,
    taxRate: data.TaxRate,
    orderNumberFormat: data.OrderNumberFormat,
    autoAssignDriver: data.AutoAssignDriver,
    firebaseServiceAccountPath: data.FirebaseServiceAccountPath,
    logLevel: data.LogLevel,
    microsoftAspNetCoreLogLevel: data.MicrosoftAspNetCoreLogLevel,
    logRetentionDays: data.LogRetentionDays,
    passwordMinLength: data.PasswordMinLength,
    passwordRequireUppercase: data.PasswordRequireUppercase,
    passwordRequireLowercase: data.PasswordRequireLowercase,
    passwordRequireNumbers: data.PasswordRequireNumbers,
    passwordRequireSpecialChars: data.PasswordRequireSpecialChars,
    passwordExpiryDays: data.PasswordExpiryDays,
    sessionTimeoutMinutes: data.SessionTimeoutMinutes,
  };
};

// Helper function to transform SystemSettings from camelCase to PascalCase
const transformSettingsToBackend = (data: UpdateSystemSettings): any => {
  return {
    AppName: data.appName,
    BaseUrl: data.baseUrl,
    DefaultLanguage: data.defaultLanguage,
    TimeZone: data.timeZone,
    DateFormat: data.dateFormat,
    TimeFormat: data.timeFormat,
    SmtpHost: data.smtpHost,
    SmtpPort: data.smtpPort,
    SmtpUsername: data.smtpUsername,
    SmtpPassword: data.smtpPassword,
    SmtpEnableSsl: data.smtpEnableSsl,
    FromEmail: data.fromEmail,
    FromName: data.fromName,
    JwtKey: data.jwtKey,
    JwtIssuer: data.jwtIssuer,
    JwtAudience: data.jwtAudience,
    JwtExpiryInMinutes: data.jwtExpiryInMinutes,
    GoogleClientId: data.googleClientId,
    GoogleClientSecret: data.googleClientSecret,
    TaxRate: data.taxRate,
    OrderNumberFormat: data.orderNumberFormat,
    AutoAssignDriver: data.autoAssignDriver,
    FirebaseServiceAccountPath: data.firebaseServiceAccountPath,
    LogLevel: data.logLevel,
    MicrosoftAspNetCoreLogLevel: data.microsoftAspNetCoreLogLevel,
    LogRetentionDays: data.logRetentionDays,
    PasswordMinLength: data.passwordMinLength,
    PasswordRequireUppercase: data.passwordRequireUppercase,
    PasswordRequireLowercase: data.passwordRequireLowercase,
    PasswordRequireNumbers: data.passwordRequireNumbers,
    PasswordRequireSpecialChars: data.passwordRequireSpecialChars,
    PasswordExpiryDays: data.passwordExpiryDays,
    SessionTimeoutMinutes: data.sessionTimeoutMinutes,
  };
};

export const systemSettingsApi = {
  getSettings: (): Promise<SystemSettings> => 
    api.get('/SystemSettings').then(res => transformSettingsFromBackend(res.data)),
  updateSettings: (data: UpdateSystemSettings): Promise<SystemSettings> => 
    api.put('/SystemSettings', transformSettingsToBackend(data)).then(res => transformSettingsFromBackend(res.data)),
  testEmail: (data: TestEmailRequest): Promise<{ success: boolean; message: string }> => 
    api.post('/SystemSettings/test-email', data).then(res => res.data),
  getSettingValue: (key: string): Promise<{ key: string; value: string }> => 
    api.get(`/SystemSettings/${key}`).then(res => res.data),
  reloadConfiguration: (): Promise<{ message: string }> => 
    api.post('/SystemSettings/reload').then(res => res.data),
};

export default api;
